import { Injectable } from '@angular/core';
import { IMqttMessage } from 'ngx-mqtt';
import { ProjectService } from 'src/app/modules/project/services/project.service';
import { BehaviorSubject, Subject, throttleTime } from 'rxjs';
import { ApiProjectService } from 'src/app/modules/project/services/http/api-project.service';
import { RESET_MQTT_QUEUE_AT_COUNT, USE_DALI } from 'src/environments/environment';
import { ApiDaliProjectService } from 'src/app/modules/project/services/http/api-dali-project.service';
import { StoredFilter } from '../models/project/stored-filter.model';
import { LocationsFilter } from '../models/locations-filter.model';
import { CurrentUserStoreService } from '../services/current-user-store.service';

@Injectable({
  providedIn: 'root'
})
export class MqttMessageQueueService {
  private ignoreMqttMessages = false;
  private mqttMessageQueue: IMqttMessage[] = [];
  private queueLength$ = new BehaviorSubject<number>(0)
  private mqttQueueIsBeingProcessed = false;

  private _refreshData$: Subject<void>;

  private get refreshData$(): Subject<void> {
    if (!this._refreshData$) {
      this._refreshData$ = new Subject<void>();
      this._refreshData$.pipe(
        throttleTime(5000)
      ).subscribe(() => this.refreshDataAndResetMqttQueue());
    }
    return this._refreshData$;
  }


  constructor(
    private projectService: ProjectService,
    private apiProjectService: ApiProjectService,
    private apiDaliProjectService: ApiDaliProjectService,
    private currentUserStoreService: CurrentUserStoreService
  ) {
  }

  async processQueue() {
    if (this.mqttQueueIsBeingProcessed) {
      // Prevent loop from starting if already active
      return
    }
    this.mqttQueueIsBeingProcessed = true;

    while (!this.ignoreMqttMessages) {
      if (this.mqttMessageQueue.length>0) {
        const msg: IMqttMessage = this.mqttMessageQueue.shift();
        this.setQueueLength(this.mqttMessageQueue.length)
        this.handleMqttMessage(msg);
      }
      // sleep
      await new Promise(r => setTimeout(r, 2));
    }

    // Mark the loop as stopped
    this.mqttQueueIsBeingProcessed = false;
  }

  public addMqttMessageToQueue(mqttMessage: IMqttMessage) {
    if (this.ignoreMqttMessages) {
      return;
    }
    this.mqttMessageQueue.push(mqttMessage);
    this.setQueueLength(this.mqttMessageQueue.length)

    if (typeof RESET_MQTT_QUEUE_AT_COUNT === 'number'
    && RESET_MQTT_QUEUE_AT_COUNT > 0 && (this.queueLength$.getValue() > RESET_MQTT_QUEUE_AT_COUNT)) {
      this.refreshData$.next()
    }
  }

  refreshDataAndResetMqttQueue() {
    if (USE_DALI) {
      this.apiDaliProjectService.getProject().subscribe();
    }
    this.apiProjectService.getAllLocationsFat().subscribe();
    const onCommonAreasPage = window.document.URL.toString().includes('common-area')
    const onRoomOverviewPage = window.document.URL.toString().includes('rooms')

    if (onCommonAreasPage) {
      // refresh common area locations
      const commonAreaFiltersForUser = StoredFilter.getCommonAreaFilterFromStorage(this.currentUserStoreService.getUser().userId);
      const commonAreaFilter = commonAreaFiltersForUser ? commonAreaFiltersForUser.filters : new LocationsFilter([2,3,4])
      this.apiProjectService.getFilteredLocationsFat(commonAreaFilter, 'commonArea').subscribe();
    }
    if (onRoomOverviewPage) {
      // refresh guest rooms locations
      const filtersForUser = StoredFilter.getGuestRoomFilterFromStorage(this.currentUserStoreService.getUser().userId);
      const filter = filtersForUser ? filtersForUser.filters : new LocationsFilter([1])
      this.apiProjectService.getFilteredLocationsFat(filter, 'guestRooms').subscribe();
    }
    this.emptyMqttQueue();
  }


  private handleMqttMessage(mqttMessage: IMqttMessage) {
    this.projectService.updateFilteredProjectByMqtt(mqttMessage);
    this.projectService.updateFullProjectByMqtt(mqttMessage)
  }

  setIgnoreMqttMessages(value: boolean) {
    this.ignoreMqttMessages = value;
  }


  setQueueLength(value: number) {
    this.queueLength$.next(value)
  }

  getQueueLength() {
    return this.queueLength$.asObservable();
  }

  emptyMqttQueue() {
    this.mqttMessageQueue = [];
    this.setQueueLength(0);
  }

}
